import { useCallback, useEffect, useState } from 'react';
import { LoaderCircle, Search } from 'lucide-react';
import { ReactComponent as CompassIcon } from '@rocco/icons/svg/compass.svg';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@rocco/components/popover';
import { Input } from '@rocco/components/input';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { LocationPlace } from '@rocco/ui/location/types/location-place';
import type { LocationFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/location-filter-state';
import {
    formatLocation,
    formatPlaceType,
    getLocationFilterPlaceholder,
} from '@rocco/utils/format-location';
import type { FilterBarElementProps } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBarElement';
import type { FilterConfigBarItemDefault } from '@rocco/ui/filter-sort/types/filter-config-bar';
import { useFilterDropdownContext } from '@rocco/ui/filter-sort/components/filters-bar/FilterDropdown';

export const FiltersBarLocation = (
    props: FilterBarElementProps<
        FilterConfigBarItemDefault,
        LocationFilterState
    >,
) => {
    const { filterState, onFilterStateUpdate } = props;
    const { searchFilters } = useFiltersContext();
    const { locationService } = searchFilters;

    const [selectedLocation, setSelectedLocation] = useState(
        filterState.selectedLocation ??
            locationService.currentLocation.location,
    );

    const { isOpen } = useFilterDropdownContext();

    const [isEditing, setEditing] = useState(false);
    const [isSuggesting, setSuggesting] = useState(false);
    const [isSettingCurrentLocation, allowToSetCurrentLocation] =
        useState(false);
    const [suggestions, setSuggestions] = useState<LocationPlace[]>([]);
    const [keyword, setKeyword] = useState('');

    const handleKeywordChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        if (event.currentTarget) {
            const value = event.currentTarget.value;

            setKeyword(value);

            if (value.trim().length > 2) {
                locationService.keywordLocations.onKeywordLocationsFetch(
                    value,
                    10,
                );
            }
        }
    };

    const resetLocationInput = useCallback(() => {
        locationService.keywordLocations.onKeywordResetPlaces();

        setKeyword('');
        setEditing(false);
        setSuggesting(false);
        setSuggestions([]);
        allowToSetCurrentLocation(false);
    }, [locationService.keywordLocations]);

    const handleSuggestionSelect = (location: LocationPlace) => {
        setSelectedLocation(location);
        resetLocationInput();
    };

    // update suggestions when keyword locations change
    useEffect(() => {
        if (isEditing && locationService.keywordLocations.locations) {
            setSuggestions(locationService.keywordLocations.locations);
            setSuggesting(true);
            allowToSetCurrentLocation(false);
        }
    }, [isEditing, locationService.keywordLocations.locations]);

    // set current location
    useEffect(() => {
        if (
            isSettingCurrentLocation &&
            locationService.currentLocation.location
        ) {
            allowToSetCurrentLocation(false);
            setSelectedLocation(locationService.currentLocation.location);
        }
    }, [locationService.currentLocation.location, isSettingCurrentLocation]);

    // update results
    useEffect(() => {
        onFilterStateUpdate(
            {
                selectedLocation: selectedLocation ?? null,
            },
            true,
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation]);

    // listen for bar filter close
    // reset suggestions, editing and suggesting
    useEffect(() => {
        if (!isOpen) {
            resetLocationInput();
        }
    }, [isOpen, resetLocationInput]);

    return (
        <div>
            <Popover
                open={isSuggesting && suggestions.length > 0}
                onOpenChange={setSuggesting}
            >
                <PopoverAnchor asChild>
                    <div className="w-full">
                        <Input
                            color="outline"
                            text="small"
                            shape="rounded"
                            placeholder={getLocationFilterPlaceholder(
                                locationService.country,
                            )}
                            role="combobox"
                            value={
                                isEditing || isSuggesting
                                    ? keyword
                                    : formatLocation(selectedLocation)
                            }
                            showClearButton={true}
                            postfix={<Search className="size-4.5" />}
                            onChange={handleKeywordChange}
                            onFocus={() => setEditing(true)}
                            onBlur={() => {
                                setEditing(false);
                                setKeyword('');
                            }}
                            onClear={resetLocationInput}
                        />
                    </div>
                </PopoverAnchor>

                <div className="mt-4 flex items-center gap-2">
                    <div
                        className="flex cursor-pointer gap-2"
                        onClick={() => {
                            allowToSetCurrentLocation(true);
                            locationService.currentLocation.onCurrentLocationFetch();
                        }}
                    >
                        <CompassIcon className="size-4.5" />
                        <span className="underline underline-offset-2">
                            Use my current location
                        </span>
                    </div>

                    {locationService.currentLocation.isLoading && (
                        <div className="flex-centered size-4 opacity-50">
                            <LoaderCircle className="size-4 animate-spin" />
                        </div>
                    )}
                </div>

                <PopoverContent
                    className="min-w-54"
                    align="start"
                    paddings="verticalSmall"
                    role="menu"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                            role="option"
                            onClick={() => handleSuggestionSelect(suggestion)}
                        >
                            <div className="text-base">
                                {formatLocation(suggestion)}
                            </div>
                            <div className="mt-0.5 text-sm text-gray-500">
                                {formatPlaceType(
                                    suggestion.placeType,
                                    locationService.country,
                                )}
                            </div>
                        </div>
                    ))}
                </PopoverContent>
            </Popover>
        </div>
    );
};
